import Link from 'next/link';

import AbTestExperiments from '@/components/ABTestDrawer/AbTestExperiments';
import CMSOverride from '@/components/Dev/CMSOverrideCookie';
import DebugErrorDevTool from '@/components/Dev/DebugErrorDevTool';
import ErrorBoundaryDevTool from '@/components/Dev/ErrorBoundaryDevTool';
import LineOfBusinessSwitch from '@/components/Dev/LineOfBusinessSwitch';
import RegionSwitch from '@/components/Dev/RegionSwitch';
import RemotesList from '@/components/Dev/RemotesList';
import { parseFederationRemotes } from '@/components/Dev/RemotesList';
import { useRegionalConfiguration } from '@/entities/regional-configuration/hooks/useRegionalConfiguration';
import useFullUser from '@/hooks/providers/useFullUser';
import useRegion from '@/hooks/providers/useRegion';
import { useAppVersion } from '@/utils/appVersion/AppVersionProvider';
import { MfeNames } from '@/utils/domains';

import { useGlobalHealth } from './useGlobalHealth';

import styles from './DevDrawer.module.css';

interface DevToolConfig {
    label: string;
    href: string;
}

interface DevToolsConfiguration {
    appName: string;
    items: DevToolConfig[];
}

const currentAppKey = (process.env.NEXT_PUBLIC_APP_NAME as MfeNames) || 'app-shell';

const quickStartSteps = [
    {
        number: 'Step 1',
        title: 'Logging In',
        href: '/dev/login',
        linkText: 'Devtool - Login Page',
        desc: 'To begin using the application, use testing credentials to login on',
    },
    {
        number: 'Step 2',
        title: 'Starting the App',
        href: '/',
        linkText: 'Starting the application',
        desc: "Now that you're logged in, let's proceed with",
    },
    {
        number: 'Optional',
        title: 'Logging Out',
        href: '/dev/logout',
        linkText: 'Devtool - Logout Page',
        desc: 'To clear cookies and log out, go to',
    },
];

const Section = ({ title, children }: React.PropsWithChildren<{ title?: string }>) => (
    <div className={styles.section}>
        {title && <h2 className={styles.sectionTitle}>{title}</h2>}
        {children}
    </div>
);

export const DevTools = ({ remoteMfe }: { remoteMfe?: string }) => {
    const { activeProfile } = useFullUser();
    const { region } = useRegion();
    const { getConfigByKey } = useRegionalConfiguration({ region });
    const devTools = getConfigByKey(`devTools.${currentAppKey}`) as
        | DevToolsConfiguration
        | undefined;
    const appDisplayName = devTools?.appName;
    const customTools: DevToolConfig[] = devTools?.items || [];
    const serverRemotes = parseFederationRemotes();

    const globalAppVersion = useAppVersion();
    const { data: health } = useGlobalHealth();
    const mfeEntries = health?.mfes ? Object.entries(health.mfes) : [];

    return (
        <div className={styles.devTools}>
            <DebugErrorDevTool />

            {/* Active User Section */}
            <Section title={appDisplayName}>
                <div className={styles.statusBar}>
                    <div className={styles.statusItem}>
                        <span className={styles.statusLabel}>Active User:</span>
                        <span className={styles.statusValue}>{activeProfile?.name || 'NONE'}</span>
                    </div>
                    {remoteMfe && remoteMfe !== currentAppKey && (
                        <div className={styles.statusItem}>
                            <span className={styles.statusLabel}>Remote MFE:</span>
                            <span className={styles.statusValue}>{remoteMfe}</span>
                        </div>
                    )}
                </div>
            </Section>

            {/* App Version */}
            <Section title="App Version">
                <div className={styles.statusBar}>
                    <div className={styles.statusItem}>
                        <span className={styles.statusLabel}>Global appVersion:</span>
                        <span className={styles.statusValue}>{globalAppVersion}</span>
                    </div>
                    {health && (
                        <>
                            <div className={styles.statusItem}>
                                <span className={styles.statusLabel}>Source:</span>
                                <span className={styles.statusValue}>
                                    {health.appVersionSource}
                                </span>
                            </div>
                            <div className={styles.statusItem}>
                                <span className={styles.statusLabel}>Environment:</span>
                                <span className={styles.statusValue}>{health.environment}</span>
                            </div>
                        </>
                    )}
                </div>
                {mfeEntries.length > 0 && (
                    <div className={styles.statusBar}>
                        {mfeEntries.map(([name, info]) => (
                            <div className={styles.statusItem} key={name}>
                                <span className={styles.statusLabel}>{name}:</span>
                                <span className={styles.statusValue}>
                                    v{info.mfeVersion} · app {info.appVersion}
                                    {info.buildNumber ? ` · build #${info.buildNumber}` : ''} ·{' '}
                                    {info.buildTimestamp}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </Section>

            {/* Switches */}
            <Section>
                <div className={styles.switchToolContainer}>
                    <RegionSwitch />
                </div>
            </Section>

            <Section>
                <div className={styles.switchToolContainer}>
                    <LineOfBusinessSwitch />
                </div>
            </Section>

            {/* Quick Start */}
            <Section title="Quick Start Guide">
                <div className={styles.steps}>
                    {quickStartSteps.map((step) => (
                        <div className={styles.step} key={step.number}>
                            <div className={styles.stepTitle}>
                                <span className={styles.stepNumber}>{step.number}:</span>{' '}
                                {step.title}
                            </div>
                            <div className={styles.stepDesc}>
                                {step.desc}{' '}
                                <Link className={styles.link} href={step.href}>
                                    {step.linkText}
                                </Link>
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Github Links */}
            <Section title="Github">
                <div className={styles.navSection}>
                    <div className={styles.navList}>
                        <a
                            className={styles.navItem}
                            href="https://github.com/paramount-streaming/web-mf-terranova-monorepo"
                            target="_blank"
                            rel="noreferrer">
                            Terra Nova
                        </a>
                        <a
                            className={styles.navItem}
                            href="https://github.com/paramount-streaming/voyager-design-system-lib"
                            target="_blank"
                            rel="noreferrer">
                            VDS
                        </a>
                    </div>
                </div>
            </Section>

            {/* CMS Overrides */}
            <Section title="CMS Overrides">
                <div className={styles.overrideContainer}>
                    <CMSOverride />
                </div>
            </Section>

            {/* Diagnostics */}
            <Section title="Diagnostics">
                <div className={styles.switchToolContainer}>
                    <ErrorBoundaryDevTool />
                </div>
            </Section>

            {/* Custom Dev Tools */}
            {customTools.length > 0 && (
                <Section title={`${appDisplayName} Tools`}>
                    <div className={styles.navSection}>
                        <div className={styles.navList}>
                            {customTools.map((tool) => (
                                <Link key={tool.href} className={styles.navItem} href={tool.href}>
                                    {tool.label}
                                </Link>
                            ))}
                        </div>
                    </div>
                </Section>
            )}

            {/* Remotes List */}
            <Section title="Remotes">
                <div className={styles.remotesContainer}>
                    <RemotesList serverRemotes={serverRemotes} />
                </div>
            </Section>

            {/* AB Tests*/}
            <Section title="AB Tests">
                <AbTestExperiments />
            </Section>
        </div>
    );
};
